import React, { useMemo, useState } from 'react';
import { formatDateString } from '../../../utils/CustomHandler';
import { renderMeetingNotes, MeetingLinkIcon, formatMsToHrMin, ExpandableText } from '../../../utils/GenerateQueryString';
import { Box, useColorMode, Badge, Text } from 'theme-ui';
import { LuPenLine } from 'react-icons/lu';
import { FaLink } from 'react-icons/fa';
import { ColumnDef } from "@tanstack/react-table";
import DataTable from '../../Tabels/Tabel';
import { HistoryEntry, HistoryTableProps } from './types';
import { bucketUrl } from '../../../utils/global';



export const HistoryTable: React.FC<HistoryTableProps> = ({ data, onEdit, type, loader, isReadOnly, onReadOnlyClick, onScrollBottom }) => {
    const [colorMode] = useColorMode();
    const isDarkMode = colorMode === 'dark';
    console.log('data', data);

    const columns = useMemo<ColumnDef<HistoryEntry>[]>(() => {
        if (type === 'CA_PHASE') {
            const caPhaseColumns: ColumnDef<HistoryEntry>[] = [
                {
                    header: () => (
                        <span style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '4px' }}>
                            No. {!isReadOnly && <LuPenLine size={14} />}
                        </span>
                    ),
                    id: "index",
                    cell: (info) => (
                        <div
                            style={{
                                cursor: !isReadOnly ? 'pointer' : 'default',
                                textDecoration: !isReadOnly ? 'underline' : 'none',
                                color: !isReadOnly ? '#0d6efd' : 'inherit',
                                width: '100%',
                                height: '100%',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                padding: '8px 0' // Added padding to ensure click target is large
                            }}
                            onClick={() => {
                                if (!isReadOnly) {
                                    onEdit(info.row.original, info.row.index);
                                }
                            }}
                        >
                            {info.row.index + 1}
                        </div>
                    ),
                },
                {
                    header: "Subject",
                    accessorKey: "cmt_subject",
                    cell: (info) => <ExpandableText text={(info.getValue() as string) || ''} maxLength={100} sx={{ textAlign: "center" }} />
                },
                {
                    header: "Comment",
                    accessorKey: "comment",
                    cell: (info) => <ExpandableText text={(info.getValue() as string) || ''} maxLength={100} sx={{ textAlign: "center" }} />
                },
                {
                    header: "FrontApp Link",
                    accessorKey: "cmt_conversation",
                    cell: (info) => {
                        const link = info.getValue() as string;
                        if (!link) return <span>-</span>;
                        return (
                            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 2, color: isDarkMode ? '#0dcaf0' : '#0d6efd' }}>
                                <FaLink size={14} />
                                <a href={link} target="_blank" rel="noreferrer" style={{ color: isDarkMode ? "#0d6efd" : 'inherit', textDecoration: 'underline' }}>LINK</a>
                            </Box>
                        );
                    },
                },
                {
                    header: "Date Received",
                    accessorFn: (row) => row.createdAt,
                    id: "createdAt",
                    cell: (info) => {
                        const date = info.getValue() as string;
                        return date ? date.split('T')[0] : '-';
                    },
                },
                {
                    header: "Due Date",
                    accessorKey: "dueDate",
                    cell: (info) => info.getValue() ? (info.getValue() as string).split('T')[0] : '-',
                },
                {
                    header: "Status",
                    accessorKey: "status",
                    cell: (info) => {
                        const val = (info.getValue() as string)?.toLowerCase() || '';
                        const isCompleted = val.includes('completed') && val !== 'notcompleted';
                        return (
                            <Badge
                                sx={{
                                    bg: isCompleted ? '#198754' : '#0d6efd',
                                    color: 'white',
                                    px: '8px',
                                    borderRadius: '6px',
                                    textTransform: 'lowercase'
                                }}
                            >
                                {isCompleted ? 'completed' : 'open'}
                            </Badge>
                        );
                    },
                },
                {
                    header: "Closed Date",
                    accessorFn: (row) => row.closeDueDate,
                    id: "closedDate",
                    cell: (info) => info.getValue() ? (info.getValue() as string).split('T')[0] : '-',
                },
                {
                    header: "Tracked Time",
                    accessorFn: (row) => row.timer?.accumulatedTime,
                    id: "trackedTime",
                    cell: (info) => {
                        const ms = info.getValue() as number;
                        if (!ms) return '-';
                        return formatMsToHrMin(ms);
                    },
                },
                {
                    header: "View",
                    id: "view",
                    cell: (info) => {
                        const attachments = info.row.original?.attachment;
                        if (!attachments || attachments.length === 0) return <span>-</span>;

                        return (
                            <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: '6px' }}>
                                {attachments.map((url, index) => {
                                    const fullUrl = `${bucketUrl}${url}`;
                                    return (
                                        <Box
                                            key={index}
                                            sx={{ display: 'flex', alignItems: 'center', gap: '4px', color: isDarkMode ? '#0dcaf0' : '#0d6efd', cursor: 'pointer' }}
                                            onClick={() => window.open(fullUrl, '_blank')}
                                        >
                                            <FaLink size={14} color="#0d6efd" />
                                            <span style={{ color: isDarkMode ? '#0d6efd' : 'inherit', textDecoration: 'underline' }}>LINK-{index + 1}</span>
                                        </Box>
                                    );
                                })}
                            </Box>
                        );
                    }
                },
                {
                    header: "Stamped As",
                    id: "stampedAs",
                    cell: (info) => (
                        <Text>{info.row.original?.stampedAs || '-'}</Text>
                    )
                }
            ];


            return caPhaseColumns;
        }

        const baseColumns: ColumnDef<HistoryEntry>[] = [
            {
                header: "Sr.No",
                id: 'srno',
                cell: (info) => info.row.index + 1,
            },
            {
                header: "Date",
                accessorKey: "meetingDate",
                cell: (info) => {
                    const dateVal = info.getValue() as string;
                    if (!dateVal) return 'N/A';
                    return formatDateString(dateVal);
                },
            },
            {
                header: "Description",
                accessorKey: "description",
                cell: (info) => <ExpandableText text={info.getValue() as string} maxLength={100} sx={{ textAlign: "left" }} />,
            },
            {
                header: type === 'MEETING' ? "Meeting Recording" : "Frontapp Link",
                accessorKey: "meetingLink",
                cell: (info) => {
                    const link = info.getValue() as string;
                    if (!link) return '-';

                    const isLink = /^(https?:\/\/|www\.|mailto:|frontapp:)/i.test(link.trim());

                    if (isLink) {
                        return (
                            <Box sx={{ display: 'flex', justifyContent: 'center' }}>
                                <MeetingLinkIcon url={link} />
                            </Box>
                        );
                    }

                    return <Box sx={{ display: 'flex', justifyContent: 'center', textAlign: 'center' }}>{link}</Box>;
                },
            }
        ];

        if (type === 'MEETING') {
            baseColumns.push({
                header: "Meeting Notes",
                accessorKey: "meetingNotes",
                cell: (info) => renderMeetingNotes(info.getValue() as string),
            });
        }

        if (!isReadOnly) {
            baseColumns.push({
                header: "Actions",
                id: 'actions',
                cell: (info) => (
                    <LuPenLine
                        style={{ cursor: 'pointer', color: 'primary', width: '20px', height: '20px' }}
                        onClick={() => onEdit(info.row.original, info.row.index)}
                    />
                ),
            });
        }

        return baseColumns;
    }, [onEdit, type, isDarkMode, isReadOnly]);

    return (
        <Box mt={3} className="history-table-wrapper" >
            <DataTable
                data={data}
                columns={columns}
                showNavigation={false}
                onPageChange={() => { }}
                onPerPageChange={() => { }}
                perPageCount={data.length || 10}
                TotalLength={data.length}
                loader={loader}
                enableColumnFilters={false}
                noDataCentered={true}
                inlineLoader={true}
                onScrollBottom={onScrollBottom}
            />
        </Box>
    );
};
